import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  isOnboardingResponseForProject,
  isProgressionDataLoading,
  progressionQueryOptions
} from '../../utils/progressionQueryUtils';
import toast from 'react-hot-toast';
import { getOperationsOnboardingMap, updateTaskForTomorrow } from '../../services/api/implementation/impl';
import {
  getOperationsPriorityNavigation,
  shouldAutoCompleteOperationsPriorityOnRedirect
} from '../../utils/operationsDailyTaskTypes';
import { ProgressionTimelineBody } from './ProgressionTimelineSteps';

const mapStepStatus = (step, currentStepId) => {
  if (step.status === 'completed') return 'completed';
  if (
    step.id === currentStepId ||
    step.status === 'current' ||
    step.status === 'delayed'
  ) {
    return 'active';
  }
  return 'locked';
};

const buildStepDescription = (step) => {
  let description = step.description || null;

  if (step.serviceProviderCount != null && step.serviceProviderMinimum != null) {
    const providerProgress = `${step.serviceProviderCount}/${step.serviceProviderMinimum} service providers added`;
    description = description ? `${description} (${providerProgress})` : providerProgress;
  }

  if (step.hasLiveTasks === false && step.id === 'create_project_tasks') {
    const liveTaskNote = 'No live tasks created yet';
    description = description ? `${description} (${liveTaskNote})` : liveTaskNote;
  }

  return description;
};

const mapApiStepsToStages = (steps = [], currentStepId) => {
  const stepTitleById = Object.fromEntries(steps.map((step) => [step.id, step.title]));

  return steps.map((step) => {
    const uiStatus = mapStepStatus(step, currentStepId);
    const needsLabel =
      step.needsLabel ||
      (uiStatus === 'locked' && step.dependsOn?.length
        ? `Needs: ${step.dependsOn.map((id) => stepTitleById[id]).filter(Boolean).join(', ')}`
        : null);

    return {
      id: step.id,
      title: step.title,
      description: buildStepDescription(step),
      needsLabel,
      status: uiStatus,
      owner:
        (typeof step.owner === 'object' ? step.owner?.name?.trim() : step.owner) || null,
      pendingDays: step.pendingDays,
      tag: step.status === 'delayed' ? 'Delayed' : step.tag,
      taskId: step.taskId,
      type: step.type,
      actions: uiStatus === 'active' && step.actionLabel ? [step.actionLabel] : []
    };
  });
};

const OperationsProgressionView = ({ projectID = null, operationsUserId = null }) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isFilterReady = projectID !== null && projectID !== undefined;
  const hasProjectSelected = isFilterReady && String(projectID) !== '';
  const resolvedProjectId = hasProjectSelected ? Number(projectID) : null;
  const hasOperationsUserSelected =
    operationsUserId !== null &&
    operationsUserId !== undefined &&
    String(operationsUserId) !== '';
  const resolvedOperationsUserId = hasOperationsUserSelected ? Number(operationsUserId) : null;

  const {
    data: onboardingResponse,
    isPending,
    isFetching,
    isError
  } = useQuery({
    queryKey: ['operationsOnboardingMap', resolvedProjectId, resolvedOperationsUserId],
    queryFn: () => getOperationsOnboardingMap(resolvedProjectId, resolvedOperationsUserId),
    enabled: hasProjectSelected && hasOperationsUserSelected,
    ...progressionQueryOptions
  });

  const isInitialLoad = isProgressionDataLoading({
    isPending,
    isFetching,
    response: onboardingResponse,
    projectId: resolvedProjectId
  });

  const stages = useMemo(() => {
    if (!isOnboardingResponseForProject(onboardingResponse, resolvedProjectId)) {
      return [];
    }

    return mapApiStepsToStages(
      onboardingResponse?.data?.steps,
      onboardingResponse?.data?.currentStepId
    );
  }, [onboardingResponse, resolvedProjectId]);

  const progressPercent = isOnboardingResponseForProject(onboardingResponse, resolvedProjectId)
    ? onboardingResponse?.data?.progress?.percent ?? 0
    : 0;

  const handleStageAction = async (stage) => {
    const navigation = getOperationsPriorityNavigation(stage.type);
    if (!navigation) return;

    if (shouldAutoCompleteOperationsPriorityOnRedirect(stage.type) && stage.taskId) {
      try {
        await updateTaskForTomorrow(stage.taskId, { status: 'Completed' });
        await queryClient.invalidateQueries({
          queryKey: ['operationsOnboardingMap', resolvedProjectId, resolvedOperationsUserId]
        });
      } catch (error) {
        toast.error(
          error?.response?.data?.message || error?.message || 'Failed to complete task'
        );
      }
    }

    navigate(navigation.path, {
      state: {
        activeTab: navigation.taskManagementTab,
        templateTab: navigation.templateTab,
        projectID: resolvedProjectId
      }
    });
  };

  return (
    <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
      <div className="mb-6 flex items-start justify-between gap-4">
        <div className="min-w-0">
          <h3 className="text-xl font-semibold text-[#1E4A53]">Operations Mission Progression</h3>
          {hasProjectSelected && (
            <p className="mt-0.5 text-xs text-gray-500">Project-specific operations onboarding</p>
          )}
        </div>
        {!isInitialLoad && !isError && hasProjectSelected && hasOperationsUserSelected && (
          <div className="shrink-0 text-right">
            <p className="text-2xl font-bold leading-none text-[#0f2a45]">{progressPercent}%</p>
            <p className="mt-1 text-[10px] font-semibold uppercase tracking-wider text-gray-400">
              Complete
            </p>
          </div>
        )}
      </div>

      {!hasProjectSelected && (
        <div className="py-12 text-center text-sm text-gray-500">
          Select a project to view operations onboarding progress.
        </div>
      )}

      {hasProjectSelected && !hasOperationsUserSelected && (
        <div className="py-12 text-center text-sm text-gray-500">
          Select an operations manager to view onboarding progress.
        </div>
      )}

      {hasProjectSelected && hasOperationsUserSelected && isInitialLoad && (
        <div className="py-12 text-center text-sm text-gray-500">
          Loading operations progression...
        </div>
      )}

      {hasProjectSelected && hasOperationsUserSelected && isError && (
        <div className="py-12 text-center text-sm text-rose-600">
          Unable to load operations progression. Please try again.
        </div>
      )}

      {hasProjectSelected &&
        hasOperationsUserSelected &&
        !isInitialLoad &&
        !isError &&
        stages.length === 0 && (
        <div className="py-12 text-center text-sm text-gray-500">
          No operations onboarding steps available for this project yet.
        </div>
      )}

      {hasProjectSelected &&
        hasOperationsUserSelected &&
        !isInitialLoad &&
        !isError &&
        stages.length > 0 && (
        <ProgressionTimelineBody stages={stages} onAction={handleStageAction} />
      )}
    </div>
  );
};

export default OperationsProgressionView;
